import React from 'react';
import { motion } from 'framer-motion';
import { Target, Users, Zap, Award, Settings } from 'lucide-react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import "../../styles/Approach.css";
import { useLanguage } from '../../context/LanguageContext';

const Approach: React.FC<{ content?: any }> = ({ content }) => {
  const { t_api } = useLanguage();
  const approachesList = content?.items || [
    {
      iconName: 'Target',
      title: t_api('Detailed Planning', 'التخطيط التفصيلي'),
      description:
        t_api('We plan every aspect of the move to determine the quickest, most cost effective approach in moving the consignment successfully...', 'نقوم بتخطيط كل جانب من جوانب النقل لتحديد الأسلوب الأسرع والأكثر فعالية من حيث التكلفة لنقل الشحنة بنجاح...'),
      gradient: 'from-blue-500 to-blue-600',
    },
  ];

  const iconMap: Record<string, any> = { Target, Users, Zap, Award, Settings };

  return (
    <section
      className="relative py-16 bg-cover bg-center text-gray-100"
      style={{ backgroundImage: `url('${content?.backgroundImage || "/Our-Approach.jpg"}')` }}
    >
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/85"></div>

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            {t_api(content?.title || "Our Proven Approach", content?.title_ar || "نهجنا المعتمد")}
          </h2>

          <p className="mt-6 text-white-700 leading-relaxed max-w-7xl mx-auto text-[15px] md:text-[16px]  text-justify md:text-center">
            {t_api(content?.intro || "HTD understands the need for detailed planning...", content?.intro_ar)}
          </p>
        </motion.div>

        {/* ✅ Swiper */}
        <Swiper
          modules={[Pagination]}
          spaceBetween={30}
          slidesPerView={3}
          pagination={{ clickable: true }}
          breakpoints={{
            0: { slidesPerView: 1 },
            640: { slidesPerView: 1 },
            768: { slidesPerView: 2 },
            1024: { slidesPerView: 3 },
          }}
          className="pb-14 custom-swiper"
        >
          {approachesList.map((approach: any, index: number) => {
            const Icon = iconMap[approach.iconName] || Target;
            return (
              <SwiperSlide key={index} className="h-full">
                <motion.div
                  className="bg-white/90 rounded-xl p-6 border border-gray-200 hover:shadow-xl transition-all duration-300 flex flex-col justify-between h-full min-h-[350px] text-gray-900"
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.2 }}
                  viewport={{ once: true }}
                >
                  <div className="flex flex-col items-center text-center flex-grow">
                    <div
                      className={`p-3 rounded-lg shadow-md mb-4 ${
                        approach.color === 'blue' ? 'bg-blue-600' :
                        approach.color === 'green' ? 'bg-emerald-600' :
                        approach.color === 'purple' ? 'bg-purple-600' :
                        approach.color === 'orange' ? 'bg-orange-600' :
                        approach.color === 'indigo' ? 'bg-indigo-600' :
                        approach.color === 'red' ? 'bg-red-600' :
                        'bg-blue-600'
                      }`}
                    >
                      <Icon className="h-6 w-6 text-white" />
                    </div>

                    <h3 className="text-lg font-semibold mb-2">{approach.title}</h3>

                    <p className="text-gray-700 text-sm leading-relaxed text-justify flex-grow">
                      {approach.description}
                    </p>
                  </div>
                </motion.div>
              </SwiperSlide>
            );
          })}
        </Swiper>

        {/* Closing Section */}
        <motion.div
          className="mt-10 text-center"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          viewport={{ once: true }}
        >
          <div
            className="text-white-700 leading-relaxed max-w-7xl mx-auto text-[15px] md:text-[16px] text-justify md:text-center mb-4"
            dangerouslySetInnerHTML={{ __html: t_api(content?.footerText || "Hamdan Transport Division has been delivering excellence...", content?.footerText_ar).replace(/\n/g, '<br/>') }}
          />
          <p className="text-[#F2BB13] text-[15px] md:text-lg font-semibold">
            {t_api(content?.footerTagline || "We are pleased to serve you!", content?.footerTagline_ar || "يسعدنا خدمتكم!")}
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default Approach;
